import clsx from "clsx";
import { Crosshair, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { Annotation, AnnotationAttitude, AnnotationIdentity, AnnotationState, Phase } from "../api/types";
import { designateSoon } from "../components/attention";
import { Checkbox } from "../components/Checkbox";
import { PropRow } from "../components/panel";
import { SaveDossierHint } from "../components/SaveDossierHint";
import { Select } from "../components/Select";
import { Button, Field, FloatingPanel, inputClass, textareaClass } from "../components/ui";
import { k, lang, t } from "../i18n/i18n";
import { SymbolThumb } from "../map/annotations/AnnotationMenu";
import { CATALOG, categoryOf, echelonLabel, fieldsOf, identityName, labelled, symbolOf } from "../map/annotations/catalog";
import { inherited, nameOf, originNote, PHASES, positionAt, useAnnotations } from "../map/annotations/useAnnotations";
import { formatMgrs } from "../map/MapCanvas";
import { refuseIfDossierLocked } from "../sharing/access";
import { useUi } from "../state/ui";
import { CardActions, CardFooter, CardTitle } from "./cardParts";
import { CommitInput } from "./GraphicCard";

/** Fiche d'une annotation APP-6 : nom (désigné après la pose : action attendue), identité, champs de sa catégorie
 *  (échelon et effectif estimés, attitude d'une population, état et protection d'un site, consignes d'un point de
 *  contrôle, GDH et bilan d'un événement), temps où elle paraît, notes ; position au temps affiché ; suppression.
 *  Chaque modification est enregistrée dans le dossier, l'ordre suit la carte et l'avis propose « Annuler ». */

const ATTITUDES: { value: AnnotationAttitude; label: string }[] = [
  { value: "favorable", label: k("Favorable") },
  { value: "neutral", label: k("Neutre") },
  { value: "hostile", label: k("Hostile") },
];
const STATES: { value: AnnotationState; label: string }[] = [
  { value: "intact", label: k("Intact") },
  { value: "damaged", label: k("Endommagé") },
  { value: "destroyed", label: k("Détruit") },
];

/** Cotation de la source et de l'information (A1 à F6), facultative. */
export function sourceProblem(text: string): string | null {
  const value = text.trim().toUpperCase();
  return !value || /^[A-F][1-6]$/.test(value) ? null : t("Cotation de A1 à F6");
}

/** Nombre entier saisi (espaces de milliers admis) ; null si vide, NaN si illisible. */
export function parseStrength(text: string): number | null {
  const compact = text.replace(/[\s  .,]/g, "");
  if (!compact) return null;
  return /^\d+$/.test(compact) ? Number(compact) : Number.NaN;
}

/** Texte de plusieurs lignes enregistré à la sortie du champ (Échap rend la valeur enregistrée). */
function CommitTextarea({
  value,
  onCommit,
  disabled,
  maxLength,
  placeholder,
}: {
  value: string;
  onCommit: (text: string) => void;
  disabled?: boolean;
  maxLength: number;
  placeholder?: string;
}) {
  const [text, setText] = useState(value);
  const cancelled = useRef(false);
  useEffect(() => setText(value), [value]);
  return (
    <textarea
      className={textareaClass}
      rows={3}
      value={text}
      disabled={disabled}
      maxLength={maxLength}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (cancelled.current) {
          cancelled.current = false;
          return;
        }
        if (text.trim() !== value.trim()) onCommit(text);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          cancelled.current = true;
          setText(value);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

export function AnnotationCard({ id }: { id: string }) {
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const requestSave = useUi((s) => s.requestSave);
  const phase = useUi((s) => s.phase);
  const focus = useUi((s) => s.annotationFocus);
  const setFocus = useUi((s) => s.setAnnotationFocus);
  const { items, canEdit, edit, remove, refuseInherited } = useAnnotations();
  const item = items.find((a) => a.id === id);

  // Annotation qui vient d'être posée : le champ « Nom » clignote et prend le focus (action attendue).
  useEffect(() => {
    if (focus !== id || !item) return;
    setFocus(null);
    // Pas d'annulation au nettoyage : la demande effacée relance l'effet, la désignation doit survivre.
    designateSoon(`[data-attention="annotation-name"] input`, "field", 1200);
  }, [focus, id, item, setFocus]);

  if (!item) return null;
  const symbol = symbolOf(item.key);
  const category = categoryOf(item.key);
  const fields = fieldsOf(item.key);
  const has = (name: string) => (fields as readonly string[]).includes(name);
  // Annotation reprise du chef : lecture seule (mention en tête de fiche, aucune modification possible).
  const fromChief = inherited(item);
  const disabled = !canEdit || fromChief;
  const at = positionAt(item, phase);
  const set = (patch: Partial<Annotation>, what: string) => edit(id, patch, what);
  const text = (name: keyof Annotation, label: string, max: number, what: string, placeholder?: string) => (
    <Field label={label}>
      <CommitInput
        value={String(item[name] ?? "")}
        maxLength={max}
        disabled={disabled}
        placeholder={placeholder}
        onCommit={(value) => set({ [name]: value.replace(/\s+/g, " ").trim() || undefined } as Partial<Annotation>, what)}
      />
    </Field>
  );
  const togglePhase = (p: Phase, on: boolean) => {
    const phases = PHASES.filter((x) => (x === p ? on : item.phases.includes(x)));
    if (!phases.length) return;
    set({ phases }, t("Temps modifiés"));
  };

  return (
    <FloatingPanel
      enter="right"
      width="card"
      tallHeader
      icon={<SymbolThumb sidc={item.sidc ?? ""} />}
      title={<CardTitle title={nameOf(item)} subtitle={`${symbol ? labelled(symbol) : item.key} · ${category ? labelled(category) : ""}`} />}
      actions={<CardActions topic="carte.annotations" />}
      footer={
        <CardFooter>
          <Button variant="secondary" size="sm" onClick={() => requestFlyTo(at, 13)}>
            <Crosshair size={14} /> {t("Centrer")}
          </Button>
          <Button
            variant="danger-ghost"
            size="sm"
            blocked={disabled}
            onBlocked={() => refuseInherited(id) || refuseIfDossierLocked() || requestSave()}
            onClick={() => remove(id)}
          >
            <Trash2 size={14} /> {t("Supprimer")}
          </Button>
        </CardFooter>
      }
    >
      {fromChief && (
        <div className="border-b border-line px-4 py-2.5" data-annotation-origin>
          <p className="text-ui font-medium leading-snug text-ink">{originNote(item)}</p>
          <p className="mt-1 text-meta leading-snug text-ink-muted">
            {t("Lecture seule : seul le dossier de l'échelon supérieur la modifie. Elle est citée dans vos ordres comme vos propres annotations.")}
          </p>
        </div>
      )}
      <div className="space-y-3 px-4 pt-3 pb-3">
        <Field label={t("Nom")}>
          <span data-attention="annotation-name" className="block">
            <CommitInput
              value={item.label ?? ""}
              placeholder={symbol ? labelled(symbol) : undefined}
              maxLength={60}
              disabled={disabled}
              ariaLabel={t("Nom de l'annotation")}
              onCommit={(value) => set({ label: value.replace(/\s+/g, " ").trim() || undefined }, t("Annotation renommée"))}
            />
          </span>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label={t("Identité")}>
            <Select
              className={inputClass}
              value={String(item.identity)}
              disabled={disabled}
              onChange={(e) => set({ identity: Number(e.target.value) as AnnotationIdentity }, t("Identité modifiée"))}
            >
              {(symbol?.identities ?? [item.identity]).map((code) => (
                <option key={code} value={code}>
                  {identityName(code)}
                </option>
              ))}
            </Select>
          </Field>
          {has("echelon") && symbol?.echelon ? (
            <Field label={t("Échelon estimé")}>
              <Select
                className={inputClass}
                value={item.echelon ?? ""}
                disabled={disabled}
                onChange={(e) => set({ echelon: e.target.value || undefined }, t("Échelon modifié"))}
              >
                <option value="">{t("Non précisé")}</option>
                {CATALOG.echelons.map((e) => (
                  <option key={e.code} value={e.code}>
                    {echelonLabel(e.code)}
                  </option>
                ))}
              </Select>
            </Field>
          ) : has("attitude") ? (
            <Field label={t("Attitude")}>
              <Select
                className={inputClass}
                value={item.attitude ?? ""}
                disabled={disabled}
                onChange={(e) => set({ attitude: (e.target.value || undefined) as AnnotationAttitude | undefined }, t("Attitude modifiée"))}
              >
                <option value="">{t("Non précisée")}</option>
                {ATTITUDES.map((a) => (
                  <option key={a.value} value={a.value}>
                    {t(a.label)}
                  </option>
                ))}
              </Select>
            </Field>
          ) : has("state") ? (
            <Field label={t("État")}>
              <Select
                className={inputClass}
                value={item.state ?? ""}
                disabled={disabled}
                onChange={(e) => set({ state: (e.target.value || undefined) as AnnotationState | undefined }, t("État modifié"))}
              >
                <option value="">{t("Non précisé")}</option>
                {STATES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {t(s.label)}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
        </div>
        {has("strength") && (
          <Field label={t("Effectif estimé")}>
            <CommitInput
              numeric
              value={item.strength != null ? item.strength.toLocaleString(lang() === "en" ? "en-GB" : "fr-FR") : ""}
              disabled={disabled}
              validate={(value) => (Number.isNaN(parseStrength(value)) ? t("Nombre entier attendu") : null)}
              onCommit={(value) => set({ strength: parseStrength(value) ?? undefined }, t("Effectif modifié"))}
            />
          </Field>
        )}
        {has("equipment") && text("equipment", t("Armement ou équipement observé"), 200, t("Équipement modifié"))}
        {has("source") && (
          <Field label={t("Cotation de la source (facultative)")}>
            <CommitInput
              value={item.source ?? ""}
              maxLength={2}
              placeholder="B2"
              disabled={disabled}
              validate={sourceProblem}
              onCommit={(value) => set({ source: value.trim().toUpperCase() || undefined }, t("Cotation modifiée"))}
            />
          </Field>
        )}
        {has("community") && text("community", t("Langue ou communauté"), 100, t("Communauté modifiée"))}
        {has("protected") && (
          <Checkbox
            checked={item.protected === true}
            disabled={disabled}
            onChange={(on) => set({ protected: on }, t("Protection modifiée"))}
            label={t("Site protégé (liste de non-frappe)")}
            detail={t("Cité au paragraphe de coordination de l'OPO.")}
          />
        )}
        {has("capacity") && text("capacity", t("Capacité"), 200, t("Capacité modifiée"))}
        {has("held_by") && text("held_by", t("Tenu par"), 60, t("Tenue modifiée"))}
        {has("instructions") && (
          <Field label={t("Consignes")}>
            <CommitTextarea
              value={item.instructions ?? ""}
              maxLength={500}
              disabled={disabled}
              onCommit={(value) => set({ instructions: value.trim() || undefined }, t("Consignes modifiées"))}
            />
          </Field>
        )}
        {has("dtg") && text("dtg", t("Moment (GDH ou temps)"), 40, t("Moment modifié"))}
        {has("toll") && text("toll", t("Bilan"), 200, t("Bilan modifié"))}
        <Field label={t("Paraît en")}>
          <div className="flex gap-4 py-1.5">
            {PHASES.map((p) => (
              <Checkbox
                key={p}
                checked={item.phases.includes(p)}
                disabled={disabled || (item.phases.length === 1 && item.phases.includes(p))}
                onChange={(on) => togglePhase(p, on)}
                label={p}
              />
            ))}
          </div>
        </Field>
        <Field label={t("Notes")}>
          <CommitTextarea
            value={item.notes ?? ""}
            maxLength={2000}
            disabled={disabled}
            onCommit={(value) => set({ notes: value.trim() || undefined }, t("Notes modifiées"))}
          />
        </Field>
      </div>
      <div className="border-t border-line px-4 py-1.5">
        <PropRow label={t("Position en {phase}", { phase })} mono>
          {formatMgrs(at[0], at[1])}
        </PropRow>
        <PropRow label={t("Position propre")}>
          <span className={clsx(!item.positions[phase] && "text-ink-muted")}>
            {item.positions[phase] ? t("Oui") : t("Celle du temps précédent")}
          </span>
        </PropRow>
      </div>
      <div className="border-t border-line px-4 py-3">
        {fromChief ? (
          <p className="text-meta leading-snug text-ink-muted">
            {t("Vous pouvez la masquer dans le panneau Calques ; vos propres annotations se posent depuis le panneau Annotations.")}
          </p>
        ) : canEdit ? (
          <p className="text-meta leading-snug text-ink-muted">
            {t("Faites glisser l'annotation pour la déplacer en {phase} ; les temps suivants sans position propre la suivent.", { phase })}
          </p>
        ) : (
          <SaveDossierHint action="modifier l'annotation" />
        )}
      </div>
    </FloatingPanel>
  );
}
